import { useState, useEffect } from 'react'
import { XMarkIcon } from '@heroicons/react/24/outline'
import { useAuth } from '@/services/authentication.service'

const DISMISSED_KEY = 'ios-install-prompt-dismissed'
const DELAY_AFTER_ONBOARDING_MS = 5 * 60 * 1000 // 5 minutes

export default function IOSInstallPrompt() {
    const { user } = useAuth()
    const [show, setShow] = useState(false)

    useEffect(() => {
        // Don't show until user has completed onboarding and been in the app for a bit
        if (!user?.onboardingComplete) return
        const accountAge = user.createdAt ? Date.now() - new Date(user.createdAt).getTime() : 0
        if (accountAge < DELAY_AFTER_ONBOARDING_MS) return

        const isIOS = /iPhone|iPad|iPod/.test(navigator.userAgent)
        const isStandalone = ('standalone' in window.navigator && (window.navigator as any).standalone) ||
            window.matchMedia('(display-mode: standalone)').matches
        const isDismissed = localStorage.getItem(DISMISSED_KEY)

        if (isIOS && !isStandalone && !isDismissed) {
            const timer = setTimeout(() => setShow(true), 2000)
            return () => clearTimeout(timer)
        }
    }, [user?.onboardingComplete])

    const dismiss = () => {
        setShow(false)
        localStorage.setItem(DISMISSED_KEY, 'true')
    }

    if (!show) return null

    return (
        <div className='fixed bottom-0 left-0 right-0 z-[60] safe-area-bottom animate-in slide-in-from-bottom duration-300'>
            <div className='mx-3 mb-3 bg-white rounded-2xl shadow-xl border border-neutral-200/60 p-4'>
                <div className='flex items-start gap-3'>
                    <img src='/pwa-192x192.png' alt='BrightPath' className='w-12 h-12 rounded-xl flex-shrink-0' />
                    <div className='flex-1 min-w-0'>
                        <p className='font-semibold text-neutral-900 text-sm'>Install BrightPath</p>
                        <p className='text-xs text-neutral-500 mt-0.5'>
                            Tap{' '}
                            <svg className='inline-block w-4 h-4 align-text-bottom text-brand-500' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'>
                                <path d='M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8' />
                                <polyline points='16 6 12 2 8 6' />
                                <line x1='12' y1='2' x2='12' y2='15' />
                            </svg>
                            {' '}then <strong>"Add to Home Screen"</strong>
                        </p>
                    </div>
                    <button
                        onClick={dismiss}
                        className='p-1 -mt-1 -mr-1 text-neutral-400 hover:text-neutral-600 rounded-lg'
                        aria-label='Dismiss'
                    >
                        <XMarkIcon className='w-5 h-5' />
                    </button>
                </div>
            </div>
        </div>
    )
}
